<template>
  <Teleport to="body">
    <Transition name="acquisition-panel">
      <div
        v-if="visible"
        class="acquisition-panel-layer"
        :style="zIndex ? { zIndex } : null"
        @click.self="$emit('close')"
      >
        <aside
          class="acquisition-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="acquisition-title"
        >
          <header class="panel-header">
            <div>
              <button
                v-if="backLabel"
                type="button"
                class="panel-back"
                @click="$emit('back')"
              >
                ← {{ backLabel }}
              </button>
              <p>Achat remporté</p>
              <h2 id="acquisition-title">
                {{ offer.title || "Achat sans titre" }}
              </h2>
            </div>
            <button type="button" aria-label="Fermer" @click="$emit('close')">
              ×
            </button>
          </header>
          <nav class="panel-tabs" aria-label="Détail de l’achat">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              type="button"
              :class="{ active: activeTab === tab.id }"
              @click="selectTab(tab.id)"
            >
              {{ tab.label }}
            </button>
          </nav>
          <div class="panel-body">
            <template v-if="activeTab === 'summary'">
              <div v-if="offerLoading" class="items-loading">
                <span></span>Chargement des informations de l’achat…
              </div>
              <template v-else>
                <section
                  class="purchase-gallery"
                  aria-label="Images de l’achat"
                >
                  <button
                    type="button"
                    class="purchase-gallery__main"
                    :disabled="!galleryImages.length"
                    aria-label="Afficher l’image en grand"
                    @click="openViewer(selectedImageIndex)"
                  >
                    <img
                      v-if="galleryImages.length"
                      :src="selectedImageUrl"
                      :alt="`Image ${selectedImageIndex + 1} de l’achat`"
                    />
                    <span v-else aria-hidden="true">◎</span>
                    <span
                      v-if="galleryImages.length"
                      class="purchase-gallery__zoom"
                      >Agrandir</span
                    >
                  </button>
                  <div
                    v-if="galleryImages.length > 1"
                    class="purchase-gallery__thumbs"
                  >
                    <button
                      v-for="(image, index) in galleryImages"
                      :key="`${image}-${index}`"
                      type="button"
                      :class="{ active: selectedImageIndex === index }"
                      :aria-label="`Afficher l’image ${index + 1}`"
                      @click="selectedImageIndex = index"
                    >
                      <img
                        :src="imageUrl(image)"
                        :alt="`Miniature ${index + 1}`"
                      />
                    </button>
                  </div>
                </section>
                <div class="offer-summary">
                  <div>
                    <span>{{ sourceLabel }}</span
                    ><small>#{{ offer._key || "—" }}</small>
                  </div>
                </div>
                <dl class="detail-list">
                  <div>
                    <dt>{{ dateLabel }}</dt>
                    <dd>{{ formattedEnd }}</dd>
                  </div>
                  <div v-if="offer.endtime">
                    <dt>
                      {{
                        offer.final_price != null
                          ? "Prix final"
                          : "Prix de l’annonce"
                      }}
                    </dt>
                    <dd>{{ yen(offer.price) }}</dd>
                  </div>
                  <div>
                    <dt>Coût enregistré</dt>
                    <dd>{{ money(totalCost) }}</dd>
                  </div>
                  <div>
                    <dt>Objets catalogués</dt>
                    <dd>{{ itemCount }}</dd>
                  </div>
                </dl>
                <section class="description">
                  <h3>Description</h3>
                  <p>
                    {{
                      offer.description ||
                      "Aucune description disponible pour cet achat."
                    }}
                  </p>
                </section>
              </template>
            </template>
            <template v-else-if="activeTab === 'costs'">
              <div class="metric-card">
                <span>Coût total enregistré</span
                ><strong>{{ money(totalCost) }}</strong>
              </div>
              <dl class="detail-list costs-list">
                <div>
                  <dt>Prix d’achat</dt>
                  <dd>{{ money(transactionCosts.price) }}</dd>
                </div>
                <div>
                  <dt>Commission</dt>
                  <dd>{{ money(transactionCosts.commission) }}</dd>
                </div>
                <div>
                  <dt>TVA</dt>
                  <dd>{{ money(transactionCosts.vat) }}</dd>
                </div>
                <div>
                  <dt>Transport local</dt>
                  <dd>{{ money(transactionCosts.localShipping) }}</dd>
                </div>
                <div v-if="offer.package_id">
                  <dt>Envoi</dt>
                  <dd>
                    <button
                      type="button"
                      class="package-link"
                      @click="$emit('open-package', offer.package_id)"
                    >
                      Colis {{ offer.packageRef }} →
                    </button>
                  </dd>
                </div>
                <div>
                  <dt>Colis</dt>
                  <dd>{{ money(transactionCosts.package) }}</dd>
                </div>
                <div>
                  <dt>Transport</dt>
                  <dd>{{ money(transactionCosts.shipping) }}</dd>
                </div>
                <div>
                  <dt>Taxes</dt>
                  <dd>{{ money(transactionCosts.taxes) }}</dd>
                </div>
              </dl>
            </template>
            <template v-else>
              <div v-if="itemsLoading" class="items-loading">
                <span></span>Chargement du contenu…
              </div>
              <p v-else-if="itemsError" class="items-feedback error">
                {{ itemsError }}
              </p>
              <template v-else-if="itemsLoaded">
                <div class="content-summary">
                  <strong>{{ itemCount }}</strong
                  ><span
                    >objet{{ itemCount > 1 ? "s" : "" }} catalogué{{
                      itemCount > 1 ? "s" : ""
                    }}</span
                  ><small
                    >{{ soldCount }} vendu{{ soldCount > 1 ? "s" : "" }}</small
                  >
                </div>
                <div v-if="items.length" class="items-list">
                  <button
                    v-for="item in items"
                    :key="item._key || item.id || item.name"
                    type="button"
                    class="entity-row"
                    :title="`Voir la fiche de ${itemName(item)}`"
                    @click="$emit('open-item', item._key || item.id)"
                  >
                    <div class="item-mark">{{ itemInitial(item) }}</div>
                    <div>
                      <strong>{{ itemName(item) }}</strong
                      ><small>{{ itemMeta(item) }}</small>
                    </div>
                    <span :class="{ sold: item.sold }">{{
                      item.sold ? "Vendu" : "En collection"
                    }}</span>
                  </button>
                </div>
                <div v-else class="items-empty">
                  <strong>Aucun objet catalogué</strong>
                  <p>
                    Ajoutez les objets contenus dans cet achat pour suivre leur
                    coût et leur revente.
                  </p>
                </div>
              </template>
            </template>
          </div>
          <footer class="panel-footer">
            <ListingLinks
              v-if="offer.links"
              :links="offer.links"
              compact
              class="panel-links"
            />
            <button
              type="button"
              class="secondary"
              @click="$emit('refresh', offer)"
            >
              Actualiser
            </button>
            <button
              type="button"
              class="secondary"
              :disabled="offerLoading"
              @click="$emit('edit', offer)"
            >
              Gérer l’achat
            </button>
            <button
              type="button"
              class="primary"
              @click="$emit('manage-items', offer)"
            >
              Gérer les objets
            </button>
          </footer>
        </aside>
      </div>
    </Transition>
    <Transition name="viewer"
      ><div
        v-if="viewerOpen"
        class="purchase-viewer"
        :style="{ zIndex: (zIndex || 1000) + 100 }"
        role="dialog"
        aria-modal="true"
        aria-label="Image de l’achat en grand"
        @click.self="viewerOpen = false"
      >
        <div class="purchase-viewer__top">
          <span>{{ viewerIndex + 1 }} / {{ galleryImages.length }}</span
          ><button
            type="button"
            aria-label="Fermer"
            @click="viewerOpen = false"
          >
            ×
          </button>
        </div>
        <button
          v-if="viewerIndex > 0"
          type="button"
          class="purchase-viewer__nav previous"
          aria-label="Image précédente"
          @click="viewerIndex--"
        >
          ‹</button
        ><img
          :src="viewerImageUrl"
          :alt="`Image ${viewerIndex + 1} de l’achat`"
        /><button
          v-if="viewerIndex < galleryImages.length - 1"
          type="button"
          class="purchase-viewer__nav next"
          aria-label="Image suivante"
          @click="viewerIndex++"
        >
          ›
        </button>
      </div></Transition
    >
  </Teleport>
</template>

<script>
import { formatDate } from "../../clock";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import config from "@/config/config.js";
import ListingLinks from "../common/ListingLinks.vue";
import { sourceLongLabel } from "../../sources";
export default {
  name: "AcquisitionQuickPanel",
  components: { ListingLinks },
  props: {
    visible: { type: Boolean, default: false },
    offer: { type: Object, default: () => ({}) },
    offerLoading: { type: Boolean, default: false },
    items: { type: Array, default: () => [] },
    itemsLoading: { type: Boolean, default: false },
    itemsLoaded: { type: Boolean, default: false },
    itemsError: { type: String, default: "" },
    // Opened from another panel (an item): a back link returns to it
    backLabel: { type: String, default: "" },
    zIndex: { type: Number, default: 0 },
  },
  emits: [
    "close",
    "back",
    "refresh",
    "edit",
    "manage-items",
    "tab-change",
    "open-item",
    "open-package",
  ],
  setup(props, { emit }) {
    const activeTab = ref("summary"),
      selectedImageIndex = ref(0),
      viewerIndex = ref(0),
      viewerOpen = ref(false);
    const tabs = [
      { id: "summary", label: "Résumé" },
      { id: "costs", label: "Coûts" },
      { id: "content", label: "Contenu" },
    ];
    const sourceLabel = computed(() => sourceLongLabel(props.offer.source));
    const galleryImages = computed(() =>
      Array.isArray(props.offer.images)
        ? props.offer.images.filter(Boolean)
        : [],
    );
    const imageUrl = (image) =>
      `${config.cdnUrl}/images/${props.offer._key}/${String(image).split(/[\\/]/).pop().split(/[?#]/)[0]}`;
    const selectedImageUrl = computed(() =>
      galleryImages.value[selectedImageIndex.value]
        ? imageUrl(galleryImages.value[selectedImageIndex.value])
        : "",
    );
    const viewerImageUrl = computed(() =>
      galleryImages.value[viewerIndex.value]
        ? imageUrl(galleryImages.value[viewerIndex.value])
        : "",
    );
    const itemCount = computed(() =>
        props.itemsLoaded
          ? props.items.length
          : Number(props.offer.items?.count) || 0,
      ),
      soldCount = computed(() =>
        props.itemsLoaded
          ? props.items.filter((item) => item.sold).length
          : Number(props.offer.items?.sold) || 0,
      );
    const transactionCosts = computed(() => props.offer.transactionCosts || {}),
      totalCost = computed(() =>
        Number(transactionCosts.value.totalCost ?? props.offer.cost ?? 0),
      );
    // Auctions: end date ; lots bought outside an auction: purchase date
    const dateLabel = computed(() =>
      props.offer.endtime || !props.offer.purchase_date
        ? "Date de fin"
        : "Date d’achat",
    );
    const formattedEnd = computed(
      () =>
        formatDate(props.offer.endtime, {
          dateStyle: "medium",
          timeStyle: "short",
        }) ||
        formatDate(props.offer.purchase_date) ||
        "Non renseignée",
    );
    const money = (value) =>
      new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(Number(value) || 0);
    const yen = (value) =>
      `${new Intl.NumberFormat("fr-FR").format(Number(value) || 0)} ¥`;
    const itemName = (item) => item.name || item.title || "Objet sans nom";
    const itemInitial = (item) => itemName(item).charAt(0).toUpperCase();
    const itemMeta = (item) =>
      [item.brand, item.model, item.condition].filter(Boolean).join(" · ") ||
      "Informations à compléter";
    const handleImageError = (event) => {
      event.target.style.visibility = "hidden";
    };
    const openViewer = (index) => {
      if (galleryImages.value.length) {
        viewerIndex.value = index;
        viewerOpen.value = true;
      }
    };
    const escape = (event) => {
      if (event.key === "Escape" && props.visible) {
        if (viewerOpen.value) viewerOpen.value = false;
        else emit(props.backLabel ? "back" : "close");
      }
      if (
        viewerOpen.value &&
        event.key === "ArrowLeft" &&
        viewerIndex.value > 0
      )
        viewerIndex.value--;
      if (
        viewerOpen.value &&
        event.key === "ArrowRight" &&
        viewerIndex.value < galleryImages.value.length - 1
      )
        viewerIndex.value++;
    };
    const selectTab = (tab) => {
      activeTab.value = tab;
      emit("tab-change", tab);
    };
    // Another offer, or the same one opened again: back to the first tab
    // (the list reloads the content only when its tab is chosen)
    watch(
      () => [props.offer._key, props.visible],
      () => {
        activeTab.value = "summary";
        selectedImageIndex.value = 0;
        viewerOpen.value = false;
      },
    );
    onMounted(() => document.addEventListener("keydown", escape));
    onUnmounted(() => document.removeEventListener("keydown", escape));
    return {
      activeTab,
      dateLabel,
      formattedEnd,
      galleryImages,
      handleImageError,
      imageUrl,
      itemCount,
      itemInitial,
      itemMeta,
      itemName,
      money,
      openViewer,
      selectedImageIndex,
      selectedImageUrl,
      selectTab,
      soldCount,
      sourceLabel,
      tabs,
      totalCost,
      transactionCosts,
      viewerImageUrl,
      viewerIndex,
      viewerOpen,
      yen,
    };
  },
};
</script>

<style scoped>
/* A row opens the sheet of its record */
.entity-row {
  width: 100%;
  border: 0;
  border-bottom: 1px solid #edf0f4;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.entity-row:hover,
.entity-row:focus-visible {
  background: #f5f8fb;
  outline: none;
}

.acquisition-panel-layer {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(15, 23, 36, 0.4);
  backdrop-filter: blur(2px);
}
.acquisition-panel {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  width: min(500px, 100%);
  height: 100%;
  flex-direction: column;
  background: #fff;
  box-shadow: -20px 0 55px rgba(15, 23, 36, 0.16);
  padding-top: var(--safe-top);
  padding-bottom: var(--safe-bottom);
}
.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 25px 26px 20px;
}
.panel-header p {
  margin: 0 0 6px;
  color: #168a64;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.panel-header h2 {
  margin: 0;
  font-size: 19px;
  line-height: 1.4;
}
.panel-header .panel-back {
  display: inline-block;
  width: auto;
  height: auto;
  margin: 0 0 12px;
  padding: 0;
  border: 0;
  background: none;
  color: #2e6098;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.panel-header button:not(.panel-back) {
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 1px solid #d9e0e8;
  border-radius: 8px;
  background: #fff;
  color: #657083;
  font-size: 22px;
}
.panel-tabs {
  display: flex;
  gap: 22px;
  padding: 0 26px;
  border-bottom: 1px solid #e4e9ef;
}
.panel-tabs button {
  position: relative;
  height: 44px;
  border: 0;
  background: none;
  color: #778294;
  font-size: 12px;
  font-weight: 700;
}
.panel-tabs button.active {
  color: #172033;
}
.panel-tabs button.active:after {
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 2px;
  background: #168a64;
  content: "";
}
.panel-body {
  flex: 1;
  overflow: auto;
  padding: 26px;
}
.offer-summary {
  display: flex;
  align-items: center;
  gap: 15px;
  padding-bottom: 24px;
}
.offer-summary img {
  width: 84px;
  height: 84px;
  object-fit: cover;
  border: 1px solid #e0e6ec;
  border-radius: 10px;
  background: #f2f5f7;
}
.offer-summary span,
.offer-summary small {
  display: block;
}
.offer-summary span {
  padding: 5px 8px;
  border-radius: 6px;
  background: #f0f3f6;
  font-size: 11px;
  font-weight: 700;
}
.offer-summary small {
  margin-top: 9px;
  color: #87919f;
}
.detail-list {
  margin: 0;
  border-top: 1px solid #e8ecf1;
}
.detail-list div {
  display: flex;
  justify-content: space-between;
  padding: 14px 0;
  border-bottom: 1px solid #edf0f4;
}
.detail-list dt {
  color: #758091;
  font-size: 12px;
}
.detail-list dd {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
}
.metric-card {
  padding: 20px;
  border: 1px solid #dfe5eb;
  border-radius: 10px;
  background: #fafcfd;
}
.metric-card span,
.metric-card strong {
  display: block;
}
.metric-card span {
  color: #758091;
  font-size: 12px;
}
.metric-card strong {
  margin-top: 7px;
  font-size: 24px;
}
.empty-copy {
  margin: 16px 0 0;
  padding: 15px;
  border-radius: 8px;
  background: #f5f8fa;
  color: #687486;
  font-size: 12px;
  line-height: 1.6;
}
.content-summary {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 20px;
  border: 1px solid #dfe5eb;
  border-radius: 10px;
}
.content-summary strong {
  font-size: 28px;
}
.content-summary span {
  font-size: 13px;
  font-weight: 700;
}
.content-summary small {
  margin-left: auto;
  color: #758091;
}
.panel-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 18px 26px;
  border-top: 1px solid #e4e9ef;
}
/* Own line above the buttons */
.package-link {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  cursor: pointer;
  color: #2e6098;
  font-weight: 700;
  text-decoration: none;
}
.panel-links {
  flex-basis: 100%;
}
.panel-footer button {
  height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
}
.panel-footer button:disabled {
  opacity: 0.5;
  cursor: wait;
}
.panel-footer .secondary {
  border: 1px solid #cfd6df;
  background: #fff;
  color: #4e596a;
}
.panel-footer .primary {
  flex: 1;
  border: 1px solid #172235;
  background: #172235;
  color: #fff;
}
.acquisition-panel-enter-active,
.acquisition-panel-leave-active {
  transition: opacity 0.2s;
}
.acquisition-panel-enter-active .acquisition-panel,
.acquisition-panel-leave-active .acquisition-panel {
  transition: transform 0.24s;
}
.acquisition-panel-enter-from,
.acquisition-panel-leave-to {
  opacity: 0;
}
.acquisition-panel-enter-from .acquisition-panel,
.acquisition-panel-leave-to .acquisition-panel {
  transform: translateX(100%);
}
.items-loading,
.items-feedback,
.items-empty {
  margin-top: 14px;
  padding: 18px;
  border-radius: 9px;
  background: #f5f8fa;
  color: #687486;
  font-size: 12px;
}
.items-loading {
  display: flex;
  align-items: center;
  gap: 9px;
}
.items-loading span {
  width: 14px;
  height: 14px;
  border: 2px solid #d7e0e7;
  border-top-color: #168a64;
  border-radius: 50%;
  animation: item-spin 0.7s linear infinite;
}
.items-feedback.error {
  background: #fff2f1;
  color: #b74640;
}
.items-empty strong {
  display: block;
  color: #394557;
  font-size: 13px;
}
.items-empty p {
  margin: 5px 0 0;
  line-height: 1.55;
}
.items-list {
  display: flex;
  flex-direction: column;
  margin-top: 14px;
  border: 1px solid #e2e7ed;
  border-radius: 9px;
}
.items-list article,
.items-list .entity-row {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 12px;
  border-bottom: 1px solid #edf0f4;
}
.items-list article:last-child,
.items-list .entity-row:last-child {
  border: 0;
}
.item-mark {
  display: grid;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  place-items: center;
  border-radius: 7px;
  background: #e9f6f1;
  color: #168a64;
  font-weight: 800;
}
.items-list article > div:nth-child(2),
.items-list .entity-row > div:nth-child(2) {
  min-width: 0;
  flex: 1;
}
.items-list strong,
.items-list small {
  display: block;
}
.items-list strong {
  overflow: hidden;
  color: #354153;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.items-list small {
  margin-top: 3px;
  color: #84909f;
  font-size: var(--meta-size);
}
.items-list article > span {
  padding: 4px 7px;
  border-radius: 6px;
  background: #edf7f3;
  color: #1d765a;
  font-size: var(--meta-size);
  font-weight: 700;
}
.items-list article > span.sold {
  background: #eef3f8;
  color: #4e6680;
}
@keyframes item-spin {
  to {
    transform: rotate(360deg);
  }
}
.purchase-gallery {
  margin-bottom: 18px;
}
.purchase-gallery__main {
  position: relative;
  display: grid;
  width: 100%;
  height: 245px;
  overflow: hidden;
  place-items: center;
  padding: 0;
  border: 1px solid #dfe5eb;
  border-radius: 10px;
  background: #f3f6f8;
  color: #87919f;
  cursor: zoom-in;
}
.purchase-gallery__main:disabled {
  cursor: default;
}
.purchase-gallery__main img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.purchase-gallery__zoom {
  position: absolute;
  right: 10px;
  bottom: 10px;
  padding: 6px 9px;
  border-radius: 6px;
  background: rgba(15, 23, 36, 0.8);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}
.purchase-gallery__thumbs {
  display: flex;
  gap: 8px;
  margin-top: 9px;
  padding-bottom: 2px;
  overflow-x: auto;
}
.purchase-gallery__thumbs button {
  width: 56px;
  height: 56px;
  flex: 0 0 56px;
  overflow: hidden;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 8px;
  background: #f3f6f8;
  cursor: pointer;
}
.purchase-gallery__thumbs button.active {
  border-color: #168a64;
}
.purchase-gallery__thumbs img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.description {
  margin-top: 22px;
  padding-top: 20px;
  border-top: 1px solid #e8ecf1;
}
.description h3 {
  margin: 0 0 10px;
  font-size: 13px;
}
.description p {
  max-height: 220px;
  margin: 0;
  overflow: auto;
  color: #657183;
  font-size: 12px;
  line-height: 1.65;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.costs-list {
  margin-top: 16px;
}
.purchase-viewer {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: grid;
  place-items: center;
  padding: 70px;
  background: rgba(8, 14, 24, 0.94);
}
.purchase-viewer > img {
  max-width: 100%;
  max-height: calc(100vh - 100px);
  object-fit: contain;
}
.purchase-viewer__top {
  position: absolute;
  top: calc(20px + var(--safe-top));
  right: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  color: #fff;
  font-size: 13px;
}
.purchase-viewer__top button,
.purchase-viewer__nav {
  display: grid;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;
}
.purchase-viewer__top button {
  width: 40px;
  height: 40px;
  border-radius: 9px;
  font-size: 25px;
}
.purchase-viewer__nav {
  position: absolute;
  top: 50%;
  width: 48px;
  height: 64px;
  border-radius: 10px;
  font-size: 40px;
  transform: translateY(-50%);
}
.purchase-viewer__nav.previous {
  left: 22px;
}
.purchase-viewer__nav.next {
  right: 22px;
}
.viewer-enter-active,
.viewer-leave-active {
  transition: opacity 0.2s;
}
.viewer-enter-from,
.viewer-leave-to {
  opacity: 0;
}
@media (max-width: 520px) {
  .purchase-gallery__main {
    height: 210px;
  }
  .purchase-viewer {
    padding: 60px 12px 20px;
  }
  .purchase-viewer__nav {
    width: 40px;
    height: 54px;
  }
  .purchase-viewer__nav.previous {
    left: 8px;
  }
  .purchase-viewer__nav.next {
    right: 8px;
  }
}
</style>
